// src/lib/notifications.ts
//
// React-side notification store. Polls /api/notifications every 30
// seconds, keeps a local cache, exposes a "mark as read" helper.
//
// Anonymous users should not be polling this endpoint — the
// Header component only mounts the bell when isAuthenticated is
// true, but the hook is also defensive and returns an empty store
// when there's no session cookie.

import { useCallback, useEffect, useRef, useState } from "react";

export type NotificationKind = "info" | "import" | "import_error" | "order" | "system";

export interface Notification {
  id: number;
  kind: NotificationKind;
  title: string;
  body: string;
  link: string;
  read_at: string | null;
  created_at: string;
}

interface State {
  items: Notification[];
  unread: number;
  loading: boolean;
  lastError: string | null;
  /** Timestamp of the most recent "unread went up" event, so the
   *  bell can pulse. Stays undefined until the first transition. */
  pulseAt: number | null;
}

const EMPTY: State = { items: [], unread: 0, loading: false,
                       lastError: null, pulseAt: null };

interface UseNotificationsResult extends State {
  refresh: () => Promise<void>;
  markRead: (ids: number[]) => Promise<void>;
  markAllRead: () => Promise<void>;
}

export function useNotifications(
  enabled: boolean,
  intervalMs = 30_000,
): UseNotificationsResult {
  const [state, setState] = useState<State>(EMPTY);
  const alive = useRef(true);

  // Track the previous unread count so we know when it transitions
  // 0 -> >0 (that's when the bell pulses + the Teddy nudge fires).
  const prevUnread = useRef<number>(0);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = await fetch("/api/notifications?limit=50", {
        credentials: "same-origin",
      });
      if (!alive.current) return;
      if (!r.ok) {
        setState((s) => ({ ...s, loading: false,
                            lastError: `HTTP ${r.status}` }));
        return;
      }
      const data = (await r.json()) as { items: Notification[];
                                          unread_count: number };
      const newUnread = data.unread_count;
      const shouldPulse = newUnread > prevUnread.current;
      prevUnread.current = newUnread;
      setState((s) => ({
        items: data.items,
        unread: newUnread,
        loading: false,
        lastError: null,
        pulseAt: shouldPulse ? Date.now() : s.pulseAt,
      }));
    } catch (e) {
      if (!alive.current) return;
      setState((s) => ({ ...s, loading: false, lastError: String(e) }));
    }
  }, [enabled]);

  const markRead = useCallback(async (ids: number[]) => {
    if (!enabled) return;
    await fetch("/api/notifications/read", {
      method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids }),
    });
    setState((s) => ({
      ...s,
      items: s.items.map((it) =>
        ids.includes(it.id) ? { ...it, read_at: new Date().toISOString() } : it),
      unread: Math.max(0, s.unread - ids.filter((i) =>
        s.items.find((x) => x.id === i && !x.read_at)).length),
    }));
  }, [enabled]);

  const markAllRead = useCallback(async () => {
    if (!enabled) return;
    await fetch("/api/notifications/read", {
      method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids: ["*"] }),
    });
    setState((s) => ({
      ...s,
      items: s.items.map((it) => ({ ...it, read_at: it.read_at
                                                       ?? new Date().toISOString() })),
      unread: 0,
    }));
  }, [enabled]);

  useEffect(() => {
    alive.current = true;
    if (!enabled) { setState(EMPTY); return; }
    void refresh();
    const t = setInterval(() => void refresh(), intervalMs);
    return () => { alive.current = false; clearInterval(t); };
  }, [enabled, intervalMs, refresh]);

  return { ...state, refresh, markRead, markAllRead };
}
